import Text from 'components/shared/Text'
import Box, { BoxProps } from 'components/shared/Box'
import DiscountedProduct from 'components/global/omnibus/DiscountedProducts/DiscountProduct'
import SeparatedList from 'components/shared/SeparatedList'
import { FormattedMessage } from 'react-intl'
import { formatDates, getDayNameAndDate } from 'helpers/dates'
import { useAppSelector } from 'store/hooks'
import { DiscountedProductAccommodation } from './DiscountProduct/DiscountedProductAccommodation'
import { useApp } from 'store/steps'
import DiscountedTickets from './DiscountedTickets'
import { useDiscountedProducts } from 'utils/hooks/useDiscountedProducts'

interface DiscountedProductsProps extends BoxProps {
    hasAdditionalExplanation?: boolean
}

const DiscountedProducts = ({
    hasAdditionalExplanation = false,
    ...props
}: DiscountedProductsProps) => {
    const { appMode, isCreatorAccommodation, isUnlimitedMode } = useApp()
    const {
        hasDiscountedProducts,
        hasDiscountedTickets,
        hasDiscountedTicketsBundles,
        hasDiscountedSpa,
        hasDiscountedBusTickets,
        hasDiscountedCabanas,
        hasDiscountedCards,
        hasDiscountedAccommodation,
        discountedTickets,
        discountedTicketsBundles,
        discountedSpa,
        discountedBusTickets,
        discountedCabanas,
        discountedCards,
        discountedTicketDates,
    } = useDiscountedProducts()

    const { items } = useAppSelector((state) => state.cart)
    const { accommodation, accommodationDetails } = items || {}

    const accommodationRooms = accommodation?.detail.rooms

    const DiscountedAccommodation = () =>
        hasDiscountedAccommodation && accommodation && accommodationDetails ? (
            <Box isFullWidth>
                <Box mb={[12, 16]}>
                    <Text variant="heading5" color="secondary_500" mb={[18, 0]}>
                        <FormattedMessage
                            id="global.summary.section_caption_accommo"
                            defaultMessage="Suntago Village"
                        />
                    </Text>
                    <Text variant="bodySmall" color="grayscale_900">
                        {formatDates(
                            [
                                accommodation.detail.dateFrom,
                                accommodation.detail.dateTo,
                            ],
                            true,
                            true,
                            true
                        )}
                    </Text>
                </Box>
                <Box gap={12} isFullWidth>
                    <DiscountedProductAccommodation
                        items={accommodationDetails.map((item) => {
                            const { rateName, occupancyName, totalPrice } = item

                            return {
                                name: rateName,
                                description: occupancyName,
                                unitPrice: totalPrice,
                            }
                        })}
                        totalPrice={accommodation.totalPrice || 0}
                        lowestPriceLast30Days={
                            accommodation.lowestPriceLast30Days || 0
                        }
                        totalPriceWithoutDiscount={
                            accommodation.totalPriceWithoutDiscount || 0
                        }
                        rooms={accommodationRooms}
                    />
                </Box>
            </Box>
        ) : null

    return !hasDiscountedProducts ? null : (
        <>
            <Box
                bgColor="grayscale_100"
                pt={24}
                pb={24}
                pl={[16, 24]}
                pr={[16, 24]}
                style={{ borderRadius: '8px' }}
                {...props}
            >
                <Box mb={[24, 32]} mt={isUnlimitedMode ? 24 : 0}>
                    <Text variant="heading4">
                        <FormattedMessage
                            id="order.discount.discounted_products_heading"
                            defaultMessage="Przecenione produkty"
                        />
                    </Text>
                    {hasAdditionalExplanation && (
                        <Text
                            mt={[8, 4]}
                            variant="bodySmall"
                            color="grayscale_900"
                        >
                            <FormattedMessage
                                id="order.discount.discounted_products_subheading"
                                defaultMessage="Zastosowaliśmy promocję do niektórych produktów w twoim zamówieniu! Porównaj ceny przed i po obniżce:"
                            />
                        </Text>
                    )}
                </Box>
                <Box gap={24} isFullWidth>
                    {isCreatorAccommodation && <DiscountedAccommodation />}

                    {(hasDiscountedTickets || hasDiscountedTicketsBundles) && (
                        <DiscountedTickets
                            dates={discountedTicketDates}
                            tickets={[
                                ...discountedTicketsBundles,
                                ...discountedTickets,
                            ]}
                        />
                    )}

                    {(hasDiscountedSpa ||
                        hasDiscountedBusTickets ||
                        hasDiscountedCabanas) && (
                        <SeparatedList
                            gap={12}
                            borderWidth={2}
                            direction="column"
                            isFullWidth
                        >
                            <Box isFullWidth>
                                <Box mb={[16, 24]}>
                                    <Text
                                        variant="heading5"
                                        color="secondary_500"
                                    >
                                        <FormattedMessage
                                            id="global.summary.section_caption_upsells"
                                            defaultMessage="Udogodnienia i atrakcje"
                                        />
                                    </Text>
                                </Box>

                                <SeparatedList
                                    gap={12}
                                    borderWidth={2}
                                    direction="column"
                                    isFullWidth
                                >
                                    {hasDiscountedSpa && (
                                        <Box gap={[12, 12]} isFullWidth>
                                            {discountedSpa.map((spa) => (
                                                <DiscountedProduct
                                                    quantity={spa.quantity || 0}
                                                    title={spa.name || ''}
                                                    subtitle={
                                                        spa.description || ''
                                                    }
                                                    valueBefore={
                                                        spa.lowestPriceLast30Days ||
                                                        0
                                                    }
                                                    valueAfter={
                                                        spa.totalPrice || 0
                                                    }
                                                    totalPriceWithoutDiscount={
                                                        spa.totalPriceWithoutDiscount ||
                                                        0
                                                    }
                                                    unitPrice={
                                                        spa.unitPrice || 0
                                                    }
                                                    key={spa.cartItemId}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                    {hasDiscountedBusTickets && (
                                        <Box gap={[12, 12]} isFullWidth>
                                            {discountedBusTickets.map(
                                                (busTicket, index) => {
                                                    // TODO: check why returnDate return null while is typed only as string

                                                    const busStartDate = `${getDayNameAndDate(
                                                        {
                                                            date: busTicket
                                                                .detail.date,
                                                            withYear: true,
                                                        }
                                                    )}`
                                                    const busEndDate =
                                                        busTicket.detail
                                                            .returnDate &&
                                                        `${getDayNameAndDate({
                                                            date: busTicket
                                                                .detail
                                                                .returnDate,
                                                            withYear: true,
                                                        })}`

                                                    const subtitle =
                                                        busStartDate ===
                                                            busEndDate ||
                                                        busEndDate === null
                                                            ? `${busStartDate}`
                                                            : `${busStartDate} - ${busEndDate}`

                                                    const valueAfter =
                                                        busTicket.quantity &&
                                                        busTicket.totalPrice
                                                            ? busTicket.totalPrice /
                                                              busTicket.quantity
                                                            : 0

                                                    return (
                                                        <DiscountedProduct
                                                            quantity={
                                                                busTicket.quantity ||
                                                                0
                                                            }
                                                            title={
                                                                busTicket.name ||
                                                                ''
                                                            }
                                                            subtitle={subtitle}
                                                            valueBefore={
                                                                busTicket.lowestPriceLast30Days ||
                                                                0
                                                            }
                                                            valueAfter={
                                                                valueAfter
                                                            }
                                                            totalPriceWithoutDiscount={
                                                                busTicket.totalPriceWithoutDiscount ||
                                                                0
                                                            }
                                                            unitPrice={
                                                                busTicket.unitPrice ||
                                                                0
                                                            }
                                                            key={index}
                                                        />
                                                    )
                                                }
                                            )}
                                        </Box>
                                    )}
                                    {hasDiscountedCabanas && (
                                        <Box gap={[12, 12]} isFullWidth>
                                            {discountedCabanas.map(
                                                (cabana, index) => {
                                                    const valueAfter =
                                                        cabana.quantity &&
                                                        cabana.totalPrice
                                                            ? cabana.totalPrice /
                                                              cabana.quantity
                                                            : 0
                                                    return (
                                                        <DiscountedProduct
                                                            quantity={
                                                                cabana.quantity ||
                                                                0
                                                            }
                                                            title={
                                                                cabana.name ||
                                                                ''
                                                            }
                                                            subtitle={
                                                                cabana.description ||
                                                                ''
                                                            }
                                                            valueBefore={
                                                                cabana.lowestPriceLast30Days ||
                                                                0
                                                            }
                                                            valueAfter={
                                                                valueAfter
                                                            }
                                                            totalPriceWithoutDiscount={
                                                                cabana.totalPriceWithoutDiscount ||
                                                                0
                                                            }
                                                            unitPrice={
                                                                cabana.unitPrice ||
                                                                0
                                                            }
                                                            key={index}
                                                        />
                                                    )
                                                }
                                            )}
                                        </Box>
                                    )}
                                </SeparatedList>
                            </Box>
                        </SeparatedList>
                    )}
                    {hasDiscountedCards && (
                        <Box isFullWidth>
                            <Box mb={[16, 24]}>
                                <Text variant="heading4" color="secondary_500">
                                    <FormattedMessage
                                        id="global.summary.section_caption_unlimited"
                                        defaultMessage="Karta całoroczna"
                                    />
                                </Text>
                            </Box>
                            <Box gap={[12, 12]} isFullWidth>
                                <SeparatedList
                                    gap={12}
                                    borderWidth={2}
                                    direction="column"
                                    isFullWidth
                                >
                                    {discountedCards.map((card, index) => (
                                        <DiscountedProduct
                                            quantity={card.quantity || 0}
                                            title={card.name || ''}
                                            subtitle={card.description || ''}
                                            valueBefore={
                                                card.lowestPriceLast30Days || 0
                                            }
                                            valueAfter={card.totalPrice || 0}
                                            totalPriceWithoutDiscount={
                                                card.totalPriceWithoutDiscount ||
                                                0
                                            }
                                            unitPrice={card.unitPrice || 0}
                                            key={index}
                                        />
                                    ))}
                                </SeparatedList>
                            </Box>
                        </Box>
                    )}

                    {appMode !== 'creator-accommodation' && (
                        <DiscountedAccommodation />
                    )}
                </Box>
            </Box>
        </>
    )
}

export default DiscountedProducts
